import Modal from '@/components/modals/Modal/Modal';
import CampaignTypesTable from '@/components/tables/CampaignTypesTable/CampaignTypesTable';
import CampaignTypesEarningsChartContainer from './CampaignTypesEarningsChartContainer';
import { WhenAreCampaignsPaidOut, WhatIsTheDifferenceBetweenCampaignTypes } from './InfoBoxes';
import type { ModalProps } from '@/types';
import { ArrowDownToLineLightGlyph } from '@acast-tech/decibel-glyphs';
import { useVisibleCategories } from '@/hooks/useVisibleCategories';
import useCampaignTypesCsvExport from './useCampaignTypesCsvExport';
import useCampaignTypesModalData from './useCampaignTypesModalData';
import { usePageMetadata } from '@/hooks';
import { getShowParam, RevenueDashboardWidget, mixpanelTrack } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';

/**
 * This component is used to display the campaign types modal.
 * @param renderTrigger - The trigger component to open the modal.
 * @returns The modal component.
 */
const CampaignTypesModal = ({ renderTrigger }: ModalProps) => {
  const { page: pageType } = usePageMetadata();
  const { handlePrefetch, selectedNetwork } = useCampaignTypesModalData();
  const { visibleCategories, setVisibleCategories } = useVisibleCategories();
  const { handleCSVExport } = useCampaignTypesCsvExport();

  const handleClose = () => {
    if (!pageType) return;

    const closedEvent =
      pageType === 'earnings'
        ? MIXPANEL_EVENT.earnings.campaignTypes.modalClosed
        : MIXPANEL_EVENT.payouts.campaignTypes.modalClosed;

    mixpanelTrack(closedEvent, {
      widget: RevenueDashboardWidget.CampaignTypes,
      page: pageType,
    });
  };

  return (
    <Modal
      hashProp="campaign-types"
      title="Campaign types"
      renderTrigger={renderTrigger}
      onPrefetch={() => {
        handlePrefetch();
      }}
      onClose={handleClose}
      showDateRangeFilter={pageType === 'earnings'}
      showShowsFilter={pageType === 'earnings'}
      downloadConfig={{
        onDownload: (dateRange, selectedShow) => handleCSVExport(dateRange, selectedShow),
        downloadButtonText: 'Export as CSV',
        downloadButtonIcon: ArrowDownToLineLightGlyph,
      }}
      onShowFilterOpen={() => {
        if (pageType === 'earnings') {
          mixpanelTrack(MIXPANEL_EVENT.earnings.campaignTypes.showFilterOpened, {
            widget: RevenueDashboardWidget.CampaignTypes,
            page: 'earnings',
          });
        }
      }}
      onShowFilterApply={(selectedShow) => {
        if (pageType === 'earnings') {
          mixpanelTrack(MIXPANEL_EVENT.earnings.campaignTypes.showFilterSelected, {
            widget: RevenueDashboardWidget.CampaignTypes,
            page: 'earnings',
            showId: getShowParam(selectedShow),
            showName: getShowParam(selectedShow),
          });
        }
      }}
      onDateRangeOpen={() => {
        if (pageType === 'earnings') {
          mixpanelTrack(MIXPANEL_EVENT.earnings.campaignTypes.dateOpened, {
            widget: RevenueDashboardWidget.CampaignTypes,
            page: 'earnings',
          });
        }
      }}
      onDateRangeApply={(dateRange) => {
        if (pageType === 'earnings') {
          mixpanelTrack(MIXPANEL_EVENT.earnings.campaignTypes.dateApplied, {
            widget: RevenueDashboardWidget.CampaignTypes,
            page: 'earnings',
            start: dateRange.from,
            end: dateRange.to,
          });
        }
      }}
      onDateRangePresetSelect={(preset) => {
        if (pageType && pageType === 'earnings') {
          mixpanelTrack(MIXPANEL_EVENT.earnings.campaignTypes.datePresetSelected, {
            widget: RevenueDashboardWidget.CampaignTypes,
            page: 'earnings',
            preset,
          });
        }
      }}
    >
      {(dateRange, selectedShow) => (
        <>
          <section className="px-6 py-10 flex flex-col gap-10">
            {pageType === 'earnings' && (
              <CampaignTypesEarningsChartContainer
                selectedNetwork={selectedNetwork}
                dateRange={dateRange}
                visibleCategories={visibleCategories}
                modalSelectedShow={selectedShow}
              />
            )}
            <CampaignTypesTable
              visibleCategories={visibleCategories}
              setVisibleCategories={setVisibleCategories}
              modalDateRange={dateRange}
              modalSelectedShow={selectedShow}
            />
            <div className="flex flex-col gap-8 mt-8">
              <WhenAreCampaignsPaidOut />
              <WhatIsTheDifferenceBetweenCampaignTypes />
            </div>
          </section>
        </>
      )}
    </Modal>
  );
};

export default CampaignTypesModal;
